import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Site} from "types/Site";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import Chip from "components/Chip";
import DeleteSite from "pages/Site/Delete";
import Map, {FullscreenControl, Layer, NavigationControl, Source} from "react-map-gl/mapbox";
import "services/mabox-gl";
import {toTitleCase} from "services/string";
import StackedList from "components/StackedList";
import StackedListItem from "components/StackedListItem";
import {Feature, FeatureCollection} from "geojson";
import {bounds} from 'services/geometry';
import {useOperator} from "context/OperatorContext";
import IconBadge from "components/IconBadge";
import IconName from "types/IconName";
import {defaultMapStyle} from "../../../components/StyleControl/MapStyles";
import {DateTime} from "luxon";
import {ExternalLinkIcon} from "@heroicons/react/outline";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";

// Helpers ----

function mapFeatures(site?: Site): { siteFeature?: Feature, zoneFeatures?: FeatureCollection } {
  if (!site) {
    return {siteFeature: undefined, zoneFeatures: undefined}
  }

  const siteFeature: Feature = {
    type: "Feature",
    id: site.id,
    properties: {name: site.name},
    geometry: site.area
  }

  const zoneFeatures: FeatureCollection = {
    type: "FeatureCollection",
    features: site ? site.zones.map(zone => ({
      type: "Feature",
      id: zone.id,
      properties: {name: zone.name},
      geometry: zone.area
    })) : []
  }

  return {siteFeature, zoneFeatures}
}

// Component ----

function ViewSite() {
  const {settings, termFor} = useOperator();
  const auth = useAuth();
  const navigate = useNavigate();
  const {id} = useParams();

  const [deletingSite, setDeletingSite] = useState<Site | null>(null);

  const {data: site, isLoading} = useQuery(
    ['site', {id}],
    () => ngage.sites.get(Number(id)),
    {enabled: auth.can('sites:read')}
  )

  const mapboxToken = settings?.integrations.mapbox.token || process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || ''
  const {siteFeature, zoneFeatures} = mapFeatures(site);

  // Prepare action handlers
  function deleteSite() {
    if (site) setDeletingSite(site)
  }

  function editSite() {
    if (id) navigate(`/sites/${id}/edit`)
  }

  // Prevent unauthorised viewing
  if (auth.cannot('sites:read')) {
    return <NotAuthorised/>
  }

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('sites:write') &&
        <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={deleteSite}>Delete</Button>
      }
      {(auth.can('sites:write') || auth.can('site-hours:write')) &&
        <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={editSite}>Edit</Button>
      }
    </>
  )

  const breadcrumbs = {
    "Locations": "/",
    [termFor('Sites')]: "/sites",
    [site?.name || `View ${termFor('Site')}`]: `/sites/${id}`
  }

  // Prepare map
  const mapViewState = {
    bounds: site ? bounds(site.area) : undefined,
  }

  return (
    <Page
      title={site?.name || `View ${termFor('Site')}`}
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >
      {site ?
        <>
          {/* Site Information */}
          <section>
            <Card
              banner={site.image}
              heading={`${termFor('Site')} Information`}
              subheading={DateTime.now().setZone(site.timezone).toHTTP()}
            >
              <DescriptionList>
                <DescriptionListItem span={2}>
                  <DescriptionTerm>About</DescriptionTerm>
                  <DescriptionDetails>
                    <p
                      className="whitespace-pre-line">{site.about ?? `A ${termFor('site')} with ${site.zone_count} ${termFor('zones')}`}</p>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Levels</DescriptionTerm>
                  <DescriptionDetails>
                    {site.levels.length < 1 &&
                      <span>None</span>
                    }

                    {site.levels.map(level => (
                      <IconBadge
                        key={level.id}
                        color={IconBadge.colors.BRAND}
                        size={IconBadge.sizes.LARGE}
                        icon={IconName.SWITCH_VERTICAL}
                        text={level.name}
                      />
                    ))}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Accessibility</DescriptionTerm>
                  <DescriptionDetails>
                    {!!site.accessibility_criteria.length && Object.values(site.accessibility_criteria).map(criterion => (
                      <IconBadge icon={IconName.HAND} text={toTitleCase(criterion)} color={IconBadge.colors.BRAND}/>
                    ))}

                    {!site.accessibility_criteria.length &&
                      <p>No criteria listed</p>
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Email Address</DescriptionTerm>
                  <DescriptionDetails>
                    {site.email ?
                      <a
                        href={`mailto:${site.email}`}
                        className="font-medium text-brand hover:text-brand/80">
                        {site.email}
                      </a> : 'Not specified'
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Phone Number</DescriptionTerm>
                  <DescriptionDetails>
                    {site.phone ?
                      <a
                        href={`tel:${site.phone}`}
                        className="font-medium text-brand hover:text-brand/80">
                        {site.phone}
                      </a> : 'Not specified'
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Address</DescriptionTerm>
                  <DescriptionDetails>
                    {!!site.address &&
                      <address>
                        {Object.values(site.address).filter(line => !!line).map((line, index) => (
                          <span key={index} className="not-italic">
                          {line}<br/>
                        </span>
                        ))}
                      </address>
                    }

                    {!site.address &&
                      <span>Not Specified</span>
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Standard Hours</DescriptionTerm>
                  <DescriptionDetails>
                    {!site.hours && 'Always Open'}

                    {!!site.hours && (
                      <StackedList>
                        {Object
                          .entries(site.hours)
                          .filter(([day]) => !['overflow', 'exceptions'].includes(day))
                          .map(([day, ranges], index) => (
                              <StackedListItem key={index}>
                                <div className="flex justify-between w-full">
                                  <div className="font-semibold">{toTitleCase(day)}</div>
                                  <div>
                                    {Array.isArray(ranges) ? (ranges.length > 0 ? ranges.join(', ') : 'Closed') : 'Closed'}
                                  </div>
                                </div>
                              </StackedListItem>
                            )
                          )
                        }
                      </StackedList>
                    )}
                  </DescriptionDetails>
                </DescriptionListItem>

                {site.links[0] &&
                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Link</DescriptionTerm>
                    <DescriptionDetails>
                      <Chip
                        name={site.links[0].text}
                        href={site.links[0].url}
                        description={site.links[0].url}
                        icon={ExternalLinkIcon}
                      />
                    </DescriptionDetails>
                  </DescriptionListItem>
                }
              </DescriptionList>
            </Card>
          </section>

          {/* Visitor Information */}
          {site.visitor_information.length ?
            <section className="mt-4">
              <Card heading="Visitor Information">
                {site.visitor_information.map((section, index) => (
                  <article key={index} className="mb-5">
                    <h3 className="text-base leading-6 font-medium text-gray-900 mb-2">{section.name}</h3>
                    <p className="text-sm whitespace-pre-line">{section.content}</p>
                  </article>
                ))}
              </Card>
            </section>
            : null}

          {/* Zones */}
          <section className="mt-4">
            <Card heading={termFor('Zones')}>
              <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {site.zones.map((zone, index) => (
                  <li key={index}>
                    <Chip
                      name={zone.name}
                      description={zone.level?.name}
                      href={`/zones/${zone.id}`}
                    />
                  </li>
                ))}
              </ul>
            </Card>
          </section>

          {/* Map */}
          <section className="mt-4 rounded-md shadow-sm overflow-hidden">
            <Map
              mapboxAccessToken={mapboxToken}
              style={{height: '32rem'}}
              mapStyle={defaultMapStyle}
              cooperativeGestures={true}
              initialViewState={mapViewState}
            >
              <NavigationControl showCompass={true} visualizePitch={true}/>
              <FullscreenControl/>

              <Source id="site" type="geojson" data={siteFeature}>
                <Layer
                  id="site"
                  type="fill"
                  paint={{'fill-color': '#1d1956', 'fill-opacity': 0.5}}
                />
                <Layer
                  id="site-name"
                  type="symbol"
                  layout={{'text-field': ['get', 'name'], 'text-anchor': 'center'}}
                  paint={{'text-halo-color': '#ececec', 'text-halo-width': 2}}
                />
              </Source>

              <Source id="zones" type="geojson" data={zoneFeatures}>
                <Layer
                  id="zones"
                  beforeId={"site-name"}
                  type="fill"
                  paint={{'fill-color': '#3730a3', 'fill-opacity': 0.5}}
                />
                <Layer
                  id="zones-name"
                  beforeId={"site-name"}
                  type="symbol"
                  layout={{'text-field': ['get', 'name'], 'text-anchor': 'center'}}
                  paint={{'text-color': '#ffffff', 'text-halo-color': '#282828', 'text-halo-width': 1}}
                />
              </Source>
            </Map>
          </section>
        </>
        : null}

      {/* Delete confirmation modal */}
      <DeleteSite
        site={deletingSite}
        show={site === deletingSite}
        onCancel={() => setDeletingSite(null)}
        onDelete={() => navigate('/sites')}
        onDeleteFail={() => setDeletingSite(null)}
      />
    </Page>
  );
}

export default ViewSite;